'use client';

/**
 * ConnectorFailuresChart — self-contained, typed React component (TypeScript).
 * Next.js-ready: client component, zero runtime deps, scoped class names (.cfc-*).
 *
 * No props. Injects its own scoped styles once, on first mount. Uses the app's
 * Poppins font (already loaded globally) — no font <link> injection.
 * Responsive via CSS container queries (4 → 2 → 1 columns) so it reflows to its
 * container's width, not the viewport — correct inside a narrow blog column.
 */
import React, { useEffect } from 'react';

/* ---------------------------- Brand assets ----------------------------- */
const PSGLOBAL_LOGO =
  'https://images.prismic.io/onepac/ag7UFaYofJOwHelQ_psglobal_color.png?auto=format,compress';

/* -------------------------------- Types -------------------------------- */
interface Stage {
  num: string;
  when: string;
  color: string;       // stage accent
  tint: string;        // watch-for box background
  tintBorder: string;  // watch-for box border
  title: React.ReactNode;
  body: string;
  watchStrong: string;
  watchLight: string;
  symptom: React.ReactNode;
}

/* -------------------------------- Data --------------------------------- */
const STAGES: Stage[] = [
  {
    num: '1', when: 'Day 1',
    color: '#04445C', tint: '#EAF1F4', tintBorder: '#D6E3EA',
    title: <>Presentment<br />currency</>,
    body: 'Shopify and NetSuite totals stop matching at the transaction level.',
    watchStrong: 'Connector collapses presentment + settlement',
    watchLight: 'into one figure.',
    symptom: <>FX deltas across thousands of orders. Two days of manual reconciliation.</>,
  },
  {
    num: '2', when: 'Week 1–2',
    color: '#0F9993', tint: '#EAF7F5', tintBorder: '#CDEAE6',
    title: <>Multi-subsidiary<br />routing</>,
    body: 'Orders post to the wrong entity, breaking consolidation and tax filings.',
    watchStrong: 'Connector maps one store to one subsidiary —',
    watchLight: 'no routing logic.',
    symptom: <>Hand-journaled corrections monthly. GST/VAT cleanup. Possible amended filings.</>,
  },
  {
    num: '3', when: 'Week 3–4',
    color: '#A8842F', tint: '#F6EFDC', tintBorder: '#E7DBB8',
    title: <>B2B and<br />wholesale logic</>,
    body: "Customer-specific catalogs, tiered pricing, and net terms hit the connector's ceiling.",
    watchStrong: 'B2B-capable tiers run 2–4× the DTC price —',
    watchLight: 'and still leave gaps.',
    symptom: <>Wholesale rollout stalls. Custom workflow built around the connector anyway.</>,
  },
  {
    num: '4', when: 'Week 6+',
    color: '#D92B03', tint: '#FDECE7', tintBorder: '#F6D2C6',
    title: <>Returns and<br />edge cases</>,
    body: 'Refund exceptions and chargebacks turn into a recurring reconciliation backlog.',
    watchStrong: 'Partial refunds, multi-currency returns, late',
    watchLight: 'PayPal chargebacks.',
    symptom: <>Orphaned records pile up. Days of unwinding, every month.</>,
  },
];

/* --------------------------- Scoped styles ----------------------------- */
const STYLE_ID = 'cfc-styles';
const CSS = `
.cfc-wrap { container-type: inline-size; width: 100%; }
.cfc-root {
  --cfc-font: 'Poppins', system-ui, -apple-system, sans-serif;
  --ink: #1B2A33; --ink-2: #3E5460; --body: #5B6B75; --muted: #8A99A3;
  --line: #E4E7EA; --line-2: #EEF1F2; --paper: #FFFFFF; --wash: #F5F8F8;
  position: relative;
  box-sizing: border-box; width: 100%; max-width: 1160px; margin: 0 auto;
  background: var(--paper); border: 1px solid var(--line); border-radius: 20px;
  box-shadow: 0 10px 20px -10px rgba(4,68,92,0.35);
  padding: 40px 44px 30px; color: var(--ink);
  font-family: var(--cfc-font); -webkit-font-smoothing: antialiased;
}
.cfc-root *, .cfc-root *::before, .cfc-root *::after { box-sizing: border-box; margin: 0; padding: 0; }
.cfc-root h1, .cfc-root h2, .cfc-root h3, .cfc-root p, .cfc-root img { margin: 0; }
.cfc-root p { letter-spacing: normal; }
.cfc-root .stage { opacity: 0; transform: translateY(14px); transition: opacity .6s ease, transform .6s cubic-bezier(0.19,1,0.22,1); }
.cfc-root .stage:nth-child(2) { transition-delay: .12s; }
.cfc-root .stage:nth-child(3) { transition-delay: .24s; }
.cfc-root .stage:nth-child(4) { transition-delay: .36s; }
.cfc-root.in .stage { opacity: 1; transform: none; }
/* Isolate from .blog-content prose styles that out-specify the universal reset: leaked h2 margins, the gold ::after underline, and p margins/letter-spacing. */
.cfc-root h1, .cfc-root h2, .cfc-root h3, .cfc-root h4, .cfc-root p { margin: 0; padding: 0; }
.cfc-root p { letter-spacing: normal; }
.cfc-root h1::after, .cfc-root h2::after, .cfc-root h3::after, .cfc-root h1::before, .cfc-root h2::before, .cfc-root h3::before { content: none !important; display: none !important; }

/* PS Global logo — always pinned to the top-right corner, never in flex flow */
.cfc-root .cfc-logo { position: absolute; top: 34px; right: 44px; height: 34px; width: auto; z-index: 2; }
.cfc-root .head { max-width: 60%; margin-top: 6px; }
.cfc-root .head h2 { font-size: 27px; font-weight: 600; letter-spacing: -0.01em; line-height: 1.2; }
.cfc-root .sub { font-size: 14px; color: var(--body); font-style: italic; margin-top: 10px; }
.cfc-root .legend { display: flex; flex-direction: column; align-items: flex-end; gap: 8px; margin-top: 22px; }
.cfc-root .legend .lbl { font-family: var(--cfc-font); font-size: 11px; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; color: var(--ink-2); }
.cfc-root .legend .bars { display: flex; width: 230px; height: 6px; border-radius: 3px; overflow: hidden; }
.cfc-root .legend .bars span { flex: 1; }

.cfc-root .grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; margin-top: 36px; }
.cfc-root .stage { display: flex; flex-direction: column; }

.cfc-root .marker { display: flex; align-items: center; gap: 10px; margin-bottom: 20px; }
.cfc-root .marker .n { flex: none; width: 34px; height: 34px; border-radius: 50%; color: #fff; font-size: 15px; font-weight: 600; display: grid; place-items: center; box-shadow: 0 0 0 4px var(--paper); }
.cfc-root .marker .pill { flex: none; color: #fff; font-family: var(--cfc-font); font-size: 11px; font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase; padding: 6px 14px; border-radius: 999px; }
.cfc-root .marker .dash { flex: 1; height: 0; border-top: 2px dashed #C6CDD2; position: relative; }
.cfc-root .stage:last-child .marker .dash::after { content: ''; position: absolute; right: -2px; top: -6px; border-left: 9px solid #AEB7BD; border-top: 5px solid transparent; border-bottom: 5px solid transparent; }

.cfc-root .card { flex: 1; display: flex; flex-direction: column; background: var(--paper); border: 1px solid var(--line); border-top: 4px solid; border-radius: 6px; padding: 24px 22px 26px; box-shadow: 0 14px 30px -22px rgba(27,42,51,0.30); }
.cfc-root .card .eyebrow { font-family: var(--cfc-font); font-size: 11px; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; }
.cfc-root .card h2 { font-size: 21px; font-weight: 600; line-height: 1.25; letter-spacing: -0.01em; color: var(--ink); margin-top: 10px; }
.cfc-root .card .tick { width: 26px; height: 3px; border-radius: 2px; margin-top: 14px; }
.cfc-root .card .body { font-size: 13.5px; line-height: 1.55; color: var(--body); margin-top: 16px; }
.cfc-root .watch { margin-top: auto; border-radius: 4px; border: 1px solid; padding: 14px 16px; margin-top: 20px; }
.cfc-root .watch .w-lbl { font-family: var(--cfc-font); font-size: 10px; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; }
.cfc-root .watch p { font-size: 12.5px; line-height: 1.5; margin-top: 7px; color: var(--ink-2); }
.cfc-root .watch p b { font-weight: 600; color: var(--ink); }
.cfc-root .watch p span { color: var(--muted); }
.cfc-root .symptom { margin-top: 18px; }
.cfc-root .symptom .s-lbl { font-family: var(--cfc-font); font-size: 10px; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; color: var(--muted); }
.cfc-root .symptom p { font-size: 12.5px; line-height: 1.55; font-style: italic; color: var(--body); margin-top: 7px; }

.cfc-root .foot { margin-top: 30px; padding-top: 16px; border-top: 1px solid var(--line-2); text-align: left; font-size: 12px; font-style: italic; color: var(--muted); }

@container (max-width: 1000px) {
  .cfc-root { padding: 32px 28px 26px; }
  .cfc-root .head { max-width: 66%; }
  .cfc-root .grid { grid-template-columns: repeat(2, 1fr); gap: 22px; }
  .cfc-root .legend .bars { width: 200px; }
}
@container (max-width: 640px) {
  .cfc-root { padding: 26px 0 22px; border-radius: 0; border-left: none; border-right: none; box-shadow: none; }
  .cfc-root .cfc-logo { display: none; }
  .cfc-root .head { max-width: 100%; margin-top: 0; }
  .cfc-root .head h2 { font-size: 21px; }
  .cfc-root .sub { font-size: 13px; }
  .cfc-root .legend { align-items: flex-start; margin-top: 18px; }
  .cfc-root .grid { grid-template-columns: 1fr; gap: 26px; margin-top: 26px; }
  .cfc-root .marker { margin-bottom: 14px; }
  .cfc-root .marker .dash, .cfc-root .stage:last-child .marker .dash::after { display: none; }
  .cfc-root .foot { text-align: left; }
}
`;

function useInjectStyles(): void {
  useEffect(() => {
    const existing = document.getElementById(STYLE_ID);
    if (existing) {
      if (existing.textContent !== CSS) existing.textContent = CSS; // refresh stale CSS after hot-reload
    } else {
      const s = document.createElement('style');
      s.id = STYLE_ID;
      s.textContent = CSS;
      document.head.appendChild(s);
    }
  }, []);
}

function useInView(ref: React.RefObject<HTMLDivElement | null>): boolean {
  const [inView, setInView] = React.useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    if (typeof IntersectionObserver === 'undefined') {
      const t = window.setTimeout(() => setInView(true), 0);
      return () => window.clearTimeout(t);
    }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) setInView(true); },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, inView]);
  return inView;
}

/* ------------------------------ Component ------------------------------ */
const ConnectorFailuresChart: React.FC = () => {
  useInjectStyles();
  const rootRef = React.useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef);

  return (
    <div className="cfc-wrap">
      <div className={`cfc-root${inView ? ' in' : ''}`} ref={rootRef}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img className="cfc-logo" src={PSGLOBAL_LOGO} alt="PS Global" />
        <div className="head">
          <h2>Where off-the-shelf connectors break</h2>
          <p className="sub">A typical APAC OneWorld + Shopify rollout</p>
        </div>
        <div className="legend">
          <span className="lbl">Severity compounds &rarr;</span>
          <div className="bars" aria-hidden="true">
            {STAGES.map((s) => <span key={s.num} style={{ background: s.color }}></span>)}
          </div>
        </div>

        <div className="grid">
          {STAGES.map((s) => (
            <div className="stage" key={s.num}>
              <div className="marker">
                <span className="n" style={{ background: s.color }}>{s.num}</span>
                <span className="pill" style={{ background: s.color }}>{s.when}</span>
                <span className="dash"></span>
              </div>
              <div className="card" style={{ borderTopColor: s.color }}>
                <span className="eyebrow" style={{ color: s.color }}>Failure 0{s.num}</span>
                <h2>{s.title}</h2>
                <span className="tick" style={{ background: s.color }}></span>
                <p className="body">{s.body}</p>
                <div className="watch" style={{ background: s.tint, borderColor: s.tintBorder }}>
                  <span className="w-lbl" style={{ color: s.color }}>Watch for</span>
                  <p><b>{s.watchStrong}</b> <span>{s.watchLight}</span></p>
                </div>
                <div className="symptom">
                  <span className="s-lbl">Symptom</span>
                  <p>{s.symptom}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="foot">Off-the-shelf connectors are built for single-entity merchants in single-currency markets.</p>
      </div>
    </div>
  );
};

export default ConnectorFailuresChart;
